// Контекст языка интерфейса. Выбранный язык — настройка вида, поэтому лежит
// отдельным ключом в браузере и не попадает ни в AppState, ни на сервер.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createStorage, type StorageAdapter } from '../store';
import { LANGS, createTranslator, type Lang, type Translate, type TranslateParams } from './translations';

export const LANG_STORAGE_KEY = 'plan-flights.lang.v1';

export type { Translate, TranslateParams };

export interface I18n {
  lang: Lang;
  setLang(lang: Lang): void;
  /**
   * Язык выбран человеком (переключателем или параметром ?lang=), а не угадан
   * по браузеру. Публичная страница поездки смотрит на это, прежде чем
   * переключиться на язык того, кто поездкой поделился: чужой выбор молча
   * перебивать нельзя, а догадку — можно.
   */
  explicit: boolean;
  /** Предложить язык. Применяется, только если своего выбора ещё не было. */
  suggestLang(lang: string): void;
  t: Translate;
}

const I18nContext = createContext<I18n | undefined>(undefined);

function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/** ?lang=es в адресе: так на сайт приходят из выдачи и по языковой ссылке. */
function queryLang(): Lang | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('lang');
  return isLang(value) ? value : null;
}

/**
 * Язык из адреса, иначе сохранённый, иначе язык браузера, иначе английский.
 * Сверяем по префиксу: navigator.language приходит вида 'es-MX', 'zh-Hant'.
 *
 * `explicit` отделяет выбор от догадки: первые два источника — это решение
 * человека, язык браузера — предположение, которое не жалко перебить.
 */
function initialLang(storage: StorageAdapter): { lang: Lang; explicit: boolean } {
  const fromQuery = queryLang();
  if (fromQuery) return { lang: fromQuery, explicit: true };
  const saved = storage.get(LANG_STORAGE_KEY);
  if (isLang(saved)) return { lang: saved, explicit: true };
  const nav = typeof navigator === 'undefined' ? '' : navigator.language.toLowerCase();
  const match = LANGS.find((code) => nav.startsWith(code));
  return { lang: match ?? 'en', explicit: false };
}

export function I18nProvider({ children }: { children: ReactNode }): ReactElement {
  const storage = useMemo(() => createStorage(), []);
  const [initial] = useState(() => initialLang(storage));
  const [lang, setLangState] = useState<Lang>(initial.lang);
  const [explicit, setExplicit] = useState(initial.explicit);

  useEffect(() => {
    storage.set(LANG_STORAGE_KEY, lang);
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [storage, lang]);

  // Тот же признак ссылкой: подсказке нужно свежее значение прямо в момент
  // вызова, а не то, что попало в замыкание при отрисовке.
  const chosen = useRef(initial.explicit);

  const setLang = useCallback((next: Lang) => {
    if (!isLang(next)) return;
    chosen.current = true;
    setExplicit(true);
    setLangState(next);
  }, []);

  // Своего выбора не было — принимаем подсказку, но выбором её не считаем:
  // переключатель остаётся свободен, и следующая подсказка тоже пройдёт.
  const suggestLang = useCallback((next: string) => {
    if (chosen.current || !isLang(next)) return;
    setLangState(next);
  }, []);

  const t = useMemo<Translate>(() => createTranslator(lang), [lang]);

  const value = useMemo<I18n>(
    () => ({ lang, setLang, explicit, suggestLang, t }),
    [lang, setLang, explicit, suggestLang, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (ctx === undefined) {
    throw new Error('useI18n() должен использоваться внутри <I18nProvider>');
  }
  return ctx;
}

/** Короткий доступ только к переводчику — самый частый случай в компонентах. */
export function useT(): Translate {
  return useI18n().t;
}
